import React from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { BREAKPOINTS, CSS_MQ } from "../../styles/breakpoints";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { ContentfulComponentCMSHook } from "@swo/shared";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import { SimpleAspectRatio } from "../_shared-components/SimpleAspectRatio";
import { PageHeadlineMedium } from "../_shared-components/ui-elements/PageHeadline";
import { ContentfulImage } from "../_shared-components/image-related/ContentfulImage";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import cn from "classnames";
import { capitalizeIfNoProductNameStyles } from "../../styles/text-styles";
import {
  EXPERIMENTAL_TextWithProductNameRenderer,
  useTextWithProductNameRenderer,
} from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { ProductPlacementType } from "@swo/storefront";
import {
  capitalizationClassName,
  withCapitalizationClassName,
} from "../../domain/swarovski-optik/products/product-name/label-contains-product-name";

export const ProductPlacementComponent: ReactTFC<ProductPlacementType & ContentfulComponentCMSHook> = props => {
  if (props.items.length !== 1 && props.items.length !== 2) {
    // Data is already validated but it's not in type signature...
    // so just to be sure it doesn't break the layout ;)
    return null;
  }

  return (
    <React.Fragment>
      {props.headline && (
        <PageHeadlineMedium
          align={"center"}
          css={css`
            margin-bottom: 20px;
            ${CSS_MQ.atLeastMd} {
              margin-bottom: 30px;
            }

            max-width: var(--max-content-width-new);
            margin-left: auto;
            margin-right: auto;
          `}
        >
          <EXPERIMENTAL_TextWithProductNameRenderer text={props.headline} />
        </PageHeadlineMedium>
      )}

      <ContentContainer
        className={cn(`items-${props.items.length}`, getComponentCMSClassname(props?.sys?.id))}
        attachStyles={css`
          display: grid;

          &.items-1 {
            grid-template-columns: repeat(1, minmax(0, 1fr));
            background-color: ${CSS_COLORS.GreenWhite};

            ${CSS_MQ.atLeastMd} {
              > * {
                width: 50%;
                margin-left: auto;
                margin-right: auto;
              }
            }
          }

          &.items-2 {
            ${CSS_MQ.atMostSm} {
              grid-template-columns: repeat(1, minmax(0, 1fr));
              grid-row-gap: 4px;
            }

            ${CSS_MQ.atLeastMd} {
              grid-template-columns: repeat(2, minmax(0, 1fr));
              grid-column-gap: 4px;
            }

            > * {
              background-color: ${CSS_COLORS.GreenWhite};
            }
          }
        `}
      >
        {props.items.map((item, idx) => (
          <ProductPlacementItem key={JSON.stringify(item)} item={item} />
        ))}
      </ContentContainer>
    </React.Fragment>
  );
};

const ProductPlacementItem: ReactTFC<{ item: ProductPlacementType["items"][0] }> = ({ item }) => {
  const { Element: DescriptiveHeadlineElement, hasAnyProductName } = useTextWithProductNameRenderer(
    item.descriptiveHeadline,
  );

  return (
    <div
      css={css`
        display: grid;
        grid-template-rows: 1fr auto auto auto;

        padding-top: 40px;
        padding-bottom: 40px;
        ${CSS_MQ.atLeastMd} {
          padding-top: 50px;
          padding-bottom: 50px;
        }

        padding-left: 20px;
        padding-right: 20px;
      `}
    >
      <SimpleAspectRatio
        css={css`
          --aspect-ratio: ${1 / 2};
          ${CSS_MQ.atLeastMd} {
            --aspect-ratio: ${1 / 3.5};
          }

          align-self: center;
          margin-bottom: 30px;
          ${CSS_MQ.atLeastMd} {
            margin-bottom: 40px;
          }
        `}
      >
        <ContentfulImage
          assetId={item.image.sys.id}
          contentfulLink={{
            url: item.image.url,
            requestedWidth: 700,
          }}
          alt={item.image.description || item.image.title || undefined}
          css={css`
            padding-left: 20px;
            padding-right: 20px;

            width: 100%;
            height: 100%;
            object-fit: contain;
          `}
        />
      </SimpleAspectRatio>

      <span
        className={withCapitalizationClassName(hasAnyProductName)}
        css={[
          FONTS.SwarovskiFutura500,
          fontColor.black,
          fontSize.x2,
          capitalizeIfNoProductNameStyles,
          css`
            text-align: center;
          `,
        ]}
      >
        {DescriptiveHeadlineElement}
      </span>
      <span
        className="u-text-balance"
        css={[
          FONTS.MinervaModern500,
          fontSize.x5,
          css`
            color: ${CSS_COLORS.Green};

            text-align: center;
            text-transform: uppercase;
          `,
        ]}
      >
        <EXPERIMENTAL_TextWithProductNameRenderer text={item.emoHeadline} />
      </span>

      <div
        css={css`
          display: flex;
          justify-content: center;

          margin-top: 20px;
          ${CSS_MQ.atLeastMd} {
            margin-top: 40px;
          }
        `}
      >
        <SwarovskiButton
          variant={"green"}
          as={"a"}
          link={item.ctaButton.data}
          isInternal={item.ctaButton.type === "Internal Link"}
          target={item.ctaButton.openInNewTab ? "_blank" : undefined}
        >
          {item.ctaButton.label}
        </SwarovskiButton>
      </div>
    </div>
  );
};
